/** @prettier */

import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Stack,
  Slider,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Fade,
  Grid,
  DialogContentText,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {format} from 'date-fns-tz';
import dayjs from 'dayjs';
import {useParams} from 'react-router-dom';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  getSettingUser,
  patchDailySchedule,
  putTodaySchedule,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {iconBlack, iconWhite, scheduleTemp, sliderMarks} from '../../define';
import {decideDayjsDatePickerLocale} from '../../functions/decideDayjsDatePickerLocale';

/**
 * スケジュール 追加編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const ScheduleSettingDialog = (props: {
  /** スケジュールを追加編集するグループのID */
  targetGroupID: string;
  /** 編集するスケジュールのデータ */
  schedule: apiIF.reqParameters.schedule;
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ 設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** スケジュール更新関数 */
  scheduleChangeHandler: () => void;
  /** 編集するスケジュールのインデックス番号（編集時のみ） */
  scheduleIndex?: number;
  /** 当日スケジュールのBodyデータ（当日スケジュールの場合のみ） */
  allTodaySchedule?: apiIF.res.getTodaySchedule['today-schedule'];
  /** 選択している特定日の種類（特定日スケジュールの場合のみ） */
  specificDayType?: keyof apiIF.res.getDailySchedule;
  /** 特定日スケジュールのレスポンスデータ（特定日スケジュールの場合のみ） */
  allDailySchedule?: apiIF.res.getDailySchedule;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  //prettier-ignore
  //スケジュール設定時間
  const [commandTime, setCommandTime] = useState<apiIF.reqParameters.schedule['command-time']>(props.schedule['command-time']);
  //prettier-ignore
  //運転/停止
  const [runStop, setRunStop] = useState<apiIF.reqParameters.schedule['run-stop']>(props.schedule['run-stop']);
  //prettier-ignore
  //運転モード
  const [operationMode, setOperationMode] = useState<apiIF.reqParameters.schedule['operation-mode']>(props.schedule['operation-mode']);
  //prettier-ignore
  //設定温度
  const [presetTemp, setPresetTemp] = useState<apiIF.reqParameters.schedule['preset-temperature']>(props.schedule['preset-temperature']);
  //prettier-ignore
  //風量
  const [airFlow, setAirFlow] = useState<apiIF.reqParameters.schedule['air-flow']>(props.schedule['air-flow']);
  //prettier-ignore
  //言語情報
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english');
  //prettier-ignore
  //設定温度未設定判定
  const [enabledTempSlider, setEnabledTempSlider] = useState(props.schedule['preset-temperature'] !== undefined);
  const [editAPIWait, setEditAPIWait] = useState(false); //ロジック設定修正API応答待ちフラグ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [isOpenDuplicateErrorDialog, setIsOpenDuplicateErrorDialog] =
    useState(false); //時刻重複エラーダイアログ表示フラグ

  //編集後に再度ダイアログを開くと変更前の値を表示する問題の解決の為に追加
  //パフォーマンスを考えるとmemo化が適切だが1stリリースを優先する
  useEffect(() => {
    if (props.isOpenDialog) {
      setCommandTime(props.schedule['command-time']);
      setRunStop(props.schedule['run-stop']);
      setOperationMode(props.schedule['operation-mode']);
      setAirFlow(props.schedule['air-flow']);

      if (props.schedule['preset-temperature'] === undefined) {
        setEnabledTempSlider(false);
      } else {
        setPresetTemp(props.schedule['preset-temperature']);
        setEnabledTempSlider(true);
      }
    }
  }, [props.isOpenDialog]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * 無効な入力状態であるか確認
   * @returns boolean
   */
  const isInvalidInput = () => {
    //動作内容が一つも設定されていない場合は無効
    const actionValues = [runStop, operationMode, presetTemp, airFlow];
    return actionValues.every(value => value === undefined);
  };

  /**
   * 指定した特定日のレスポンスデータを編集し、Bodyデータに変形する
   * @param correctedSchedule 修正データ
   * @param specificDayType 修正する特定日
   * @param allSchedule 特定日スケジュールのレスポンスデータ
   * @returns 特定日スケジュールの修正後のBodyデータ
   */
  const dailyScheduleToBodyData = (
    correctedSchedule: apiIF.reqParameters.schedule,
    specificDayType: keyof apiIF.reqParameters.patchDailySchedule,
    allSchedule: apiIF.res.getDailySchedule
  ) => {
    //修正前のBodyデータを初期値に設定
    let settingBodyData: apiIF.reqParameters.patchDailySchedule = {};

    //prettier-ignore
    //4つの特定日をループ
    for (const key of Object.keys(allSchedule) as (keyof apiIF.res.getDailySchedule)[]) {
      //編集する特定日タイプ以外は削除する
      if (key !== specificDayType) {
        continue;
      }
      //直接操作するとAPIエラー時にDBにはスケジュールは未反映だが
      //表示上はスケジュールの修正や追加が反映されたかのように見えるためコピーして操作する
      const copiedSchedule = [...allSchedule[key]];

      if (props.scheduleIndex !== undefined) {
        //スケジュールのindex番号が指定されている場合、スケジュール編集
        copiedSchedule[props.scheduleIndex] = correctedSchedule;
      } else {
        //スケジュールのindex番号が指定されていない場合、スケジュール追加
        copiedSchedule.push(correctedSchedule);
      }
      settingBodyData[key] = {'schedule' :  copiedSchedule}
    }
    return settingBodyData;
  };

  /**
   * 当日スケジュールのリクエストBodyを書き換える
   * @param correctedSchedule 修正データ
   * @param allSchedule 修正前のBodyデータ
   * @returns 修正データを反映したBodyデータ
   */
  const todayScheduleToDataBody = (
    correctedSchedule: apiIF.reqParameters.schedule,
    allSchedule: apiIF.res.getTodaySchedule['today-schedule']
  ) => {
    //修正前のBodyデータを初期値に設定
    let settingBodyData: apiIF.reqParameters.putTodaySchedule = {
      //直接操作するとAPIエラー時にDBにはスケジュールは未反映だが
      //表示上はスケジュールの修正や追加が反映されたかのように見えるためコピーして操作する
      'today-schedule': [...allSchedule],
    };

    if (props.scheduleIndex !== undefined) {
      //スケジュール番号の指定がある場合、スケジュール編集
      settingBodyData['today-schedule'][props.scheduleIndex] =
        correctedSchedule;
    } else {
      //スケジュール番号の指定がない場合、スケジュール追加
      settingBodyData['today-schedule'].push(correctedSchedule);
    }
    return settingBodyData;
  };

  /**
   * Command time（スケジュール時間）変更処理
   * @param date カレンダーで選択した日付
   */
  const handleTimeChange = (date: dayjs.Dayjs | null) => {
    if (date === null) return;
    const time = format(date.toDate(), 'HH:mm'); //commandTimeの型に整形する
    setCommandTime(time);
  };

  /**
   * 運転/停止変更時処理
   * @param event マウスイベント
   * @param newAlignment ボタンが押された運転/停止
   */
  const handlePowerChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'run': //fall through
      case 'stop':
        setRunStop(newAlignment);
        break;
      default:
        //規定の運転停止以外の場合は未設定
        setRunStop(undefined);
    }
  };

  /**
   * 運転モード変更時処理
   * @param event マウスイベント
   * @param newAlignment ボタンが押された運転モード
   */
  const handleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'auto': //fall through
      case 'cooling': //fall through
      case 'heating': //fall through
      case 'fan': //fall through
      case 'dehumidify':
        setOperationMode(newAlignment);
        break;
      default:
        //規定のモード以外の場合は未設定
        setOperationMode(undefined);
    }
    //heatingから運転モードを切り替えた際、設定温度が18℃未満の場合
    //設定温度を18℃に設定する
    if (
      newAlignment !== 'heating' &&
      Number(presetTemp) < scheduleTemp.SET_TEMP_MIN
    ) {
      setPresetTemp(scheduleTemp.SET_TEMP_MIN.toFixed(1));
    }
  };

  /**
   * Parameter（設定温度スライダー）有効無効変更時処理
   * @param event トグルボタンON/OFF
   */
  const handleNoSetTemperature = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEnabledTempSlider(event.target.checked);

    if (event.target.checked && operationMode === 'heating') {
      setPresetTemp(scheduleTemp.SET_TEMP_HEATING_MIN.toFixed(1));
    } else if (event.target.checked) {
      setPresetTemp(scheduleTemp.SET_TEMP_MIN.toFixed(1));
    } else {
      setPresetTemp(undefined);
    }
  };
  /**
   * Parameter（設定温度）スライダードラッグ時処理
   * @param event スライダーのドラッグ
   * @param newValue 設定値
   */
  const handlePresetTemperatureChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setPresetTemp(String((newValue as number).toFixed(1)));
  };
  /**
   * Parameter（設定温度）スライダードロップ時処理
   * @param event スライダーのドロップ
   * @param newValue 設定値
   */
  const handlePresetTemperatureChangeCommitted = (
    event: Event | React.SyntheticEvent<Element, Event>,
    newValue: number | number[]
  ) => {
    setPresetTemp(String((newValue as number).toFixed(1)));
  };

  /**
   * 風量変更時処理
   * @param event マウスイベント
   * @param newAlignment ボタンが押された風量
   */
  const handleAirFlowChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'auto': //fall through
      case 'low': //fall through
      case 'middle': //fall through
      case 'high': //fall through
      case 'powerful':
        setAirFlow(newAlignment);
        break;
      default:
        //規定のモード以外の場合は未設定
        setAirFlow(undefined);
    }
  };

  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    if (siteID === undefined) return;

    setEditAPIWait(true);
    //修正データの作成
    let tempScheduleData: apiIF.reqParameters.schedule = {
      'command-time': commandTime,
    };
    //設定値がundefinedの場合はBodyデータに含めない
    if (runStop !== undefined) tempScheduleData['run-stop'] = runStop;
    //prettier-ignore
    if (operationMode !== undefined) tempScheduleData['operation-mode'] = operationMode;
    if (airFlow !== undefined) tempScheduleData['air-flow'] = airFlow;
    //設定温度スライダーが無効の場合はBodyデータに含めない
    if (enabledTempSlider) tempScheduleData['preset-temperature'] = presetTemp;

    if (props.allTodaySchedule !== undefined) {
      // 同時刻の設定が存在していないかを確認
      const duplicateCheck = props.allTodaySchedule.some(schedule => {
        if (
          //編集モードであり
          props.scheduleIndex !== undefined &&
          //時刻データに修正がない場合
          commandTime === props.schedule['command-time']
        ) {
          //時刻の重複チェックは不要
          return false;
        }

        return schedule['command-time'] === commandTime;
      });
      // 同時刻の設定が存在している場合はエラー
      if (duplicateCheck) {
        setIsOpenDuplicateErrorDialog(true);
        setEditAPIWait(false);
        return;
      }
      //当日スケジュールを編集する場合
      //編集したスケジュールをBodyデータに反映する
      const todayScheduleData = todayScheduleToDataBody(
        tempScheduleData,
        props.allTodaySchedule
      );
      //当日スケジュールを編集
      putTodaySchedule(siteID, props.targetGroupID, todayScheduleData)
        .then(response => {
          props.scheduleChangeHandler();
          handleClose();
        })
        .catch(err => {
          apiErrorAlert(trans);
        })
        .finally(() => {
          setEditAPIWait(false);
        });
    } else if (
      props.allDailySchedule !== undefined &&
      props.specificDayType !== undefined
    ) {
      // 同時刻の設定が存在していないかを確認
      const duplicateCheck = props.allDailySchedule[props.specificDayType].some(
        schedule => {
          if (
            //編集モードであり
            props.scheduleIndex !== undefined &&
            //時刻データに修正がない場合
            commandTime === props.schedule['command-time']
          ) {
            //時刻の重複チェックは不要
            return false;
          }

          return schedule['command-time'] === commandTime;
        }
      );
      // 同時刻の設定が存在している場合はエラー
      if (duplicateCheck) {
        setIsOpenDuplicateErrorDialog(true);
        setEditAPIWait(false);
        return;
      }
      //特定日スケジュールを編集する場合
      //レスポンスデータを編集してBodyデータの形にする
      const dailyScheduleData = dailyScheduleToBodyData(
        tempScheduleData,
        props.specificDayType,
        props.allDailySchedule
      );
      //特定日スケジュールを編集
      patchDailySchedule(siteID, props.targetGroupID, dailyScheduleData)
        .then(response => {
          props.scheduleChangeHandler();
          handleClose();
        })
        .catch(err => {
          apiErrorAlert(trans);
        })
        .finally(() => {
          setEditAPIWait(false);
        });
    }
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setCommandTime(props.schedule['command-time']);
    setRunStop(props.schedule['run-stop']);
    setOperationMode(props.schedule['operation-mode']);
    setAirFlow(props.schedule['air-flow']);
    setPresetTemp(props.schedule['preset-temperature']);
    if (props.schedule['preset-temperature'] === undefined) {
      //引数のデータの設定温度がundefinedの場合、スライダーを無効化
      setEnabledTempSlider(false);
    } else {
      //引数のデータの設定温度がundefinedでない場合、スライダーを有効化
      setEnabledTempSlider(true);
    }
    props.setIsOpenDialog(false);
  };

  /**
   * 時刻重複ダイアログOKボタン押下時処理
   */
  const handleDuplicateErrorClose = () => {
    setIsOpenDuplicateErrorDialog(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={props.isOpenDialog} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('ScheduleSettingDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__editDialog__contents">
          <Grid container rowSpacing={4}>
            {/** 時刻 */}
            <Grid item xs={12}>
              <Stack spacing={2}>
                <Box className="schedule__editDialog__contents__title">
                  {trans('ScheduleSettingDialog.list.time')}
                </Box>
                <Box className="schedule__editDialog__contents__time">
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={decideDayjsDatePickerLocale(language)}>
                    <TimePicker
                      ampm={false}
                      timeSteps={{minutes: 1}}
                      value={dayjs('2000-01-01T' + commandTime)} //年月日はピッカーの表示設定に合わせるために追加
                      onChange={date => handleTimeChange(date)}
                      slotProps={{field: {readOnly: true}}}
                    />
                  </LocalizationProvider>
                </Box>
              </Stack>
            </Grid>
            {/** 指令値 */}
            <Grid item xs={12}>
              <Stack spacing={3}>
                <Box className="schedule__editDialog__contents__title">
                  {trans('ScheduleSettingDialog.list.action')}
                </Box>
                <Box className="schedule__editDialog__contents__action">
                  <Stack spacing={2}>
                    {/** 運転/停止 */}
                    <Stack spacing={1}>
                      <Box>{trans('ScheduleSettingDialog.action.power')} :</Box>
                      <ToggleButtonGroup
                        value={runStop}
                        exclusive
                        onChange={handlePowerChange}
                        aria-label="Run stop change"
                        className="schedule__editDialog__contents__action__runStop__toggleButton">
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.power.on'
                          )}`}
                          value="run"
                          aria-label="on"
                          className={
                            runStop === 'run'
                              ? 'schedule__editDialog__contents__action__runStop__toggleButton--selected'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectRunStopRun"
                            className="schedule__editDialog__contents__action__runStop__toggleButton__image"
                            src={
                              runStop === 'run'
                                ? iconWhite.AC_ON.src
                                : iconBlack.AC_ON.src
                            }
                            alt={
                              runStop === 'run'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.AC_ON.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.AC_ON.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.power.off'
                          )}`}
                          value="stop"
                          aria-label="off"
                          className={
                            runStop === 'stop'
                              ? 'schedule__editDialog__contents__action__runStop__toggleButton--selected'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectRunStopStop"
                            className="schedule__editDialog__contents__action__runStop__toggleButton__image"
                            src={
                              runStop === 'stop'
                                ? iconWhite.AC_OFF.src
                                : iconBlack.AC_OFF.src
                            }
                            alt={
                              runStop === 'stop'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.AC_OFF.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.AC_OFF.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                    {/** 運転モード */}
                    <Stack spacing={1}>
                      <Box>{trans('ScheduleSettingDialog.action.mode')} :</Box>
                      <ToggleButtonGroup
                        value={operationMode}
                        exclusive
                        onChange={handleModeChange}
                        aria-label="Operation mode change"
                        className="schedule__editDialog__contents__action__mode__toggleButton">
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.mode.auto'
                          )}`}
                          value="auto"
                          aria-label="auto"
                          className={
                            operationMode === 'auto'
                              ? 'schedule__editDialog__contents__action__mode__toggleButton--selectedAuto'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectOperationModeAuto"
                            className="schedule__editDialog__contents__action__mode__toggleButton__image"
                            src={
                              operationMode === 'auto'
                                ? iconWhite.AUTO.src
                                : iconBlack.AUTO.src
                            }
                            alt={
                              operationMode === 'auto'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.AUTO.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.AUTO.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.mode.cooling'
                          )}`}
                          value="cooling"
                          aria-label="cooling"
                          className={
                            operationMode === 'cooling'
                              ? 'schedule__editDialog__contents__action__mode__toggleButton--selectedCooling'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectOperationModeCooling"
                            className="schedule__editDialog__contents__action__mode__toggleButton__image"
                            src={
                              operationMode === 'cooling'
                                ? iconWhite.COOLING.src
                                : iconBlack.COOLING.src
                            }
                            alt={
                              operationMode === 'cooling'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.COOLING.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.COOLING.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.mode.heating'
                          )}`}
                          value="heating"
                          aria-label="heating"
                          className={
                            operationMode === 'heating'
                              ? 'schedule__editDialog__contents__action__mode__toggleButton--selectedHeating'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectOperationModeHeating"
                            className="schedule__editDialog__contents__action__mode__toggleButton__image"
                            src={
                              operationMode === 'heating'
                                ? iconWhite.HEATING.src
                                : iconBlack.HEATING.src
                            }
                            alt={
                              operationMode === 'heating'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.HEATING.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.HEATING.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.mode.fan'
                          )}`}
                          value="fan"
                          aria-label="fan"
                          className={
                            operationMode === 'fan'
                              ? 'schedule__editDialog__contents__action__mode__toggleButton--selectedFan'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectOperationModeFan"
                            className="schedule__editDialog__contents__action__mode__toggleButton__image"
                            src={
                              operationMode === 'fan'
                                ? iconWhite.BLOWER.src
                                : iconBlack.BLOWER.src
                            }
                            alt={
                              operationMode === 'fan'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.BLOWER.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.BLOWER.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.mode.dry'
                          )}`}
                          value="dehumidify"
                          aria-label="dehumidify"
                          className={
                            operationMode === 'dehumidify'
                              ? 'schedule__editDialog__contents__action__mode__toggleButton--selectedDry'
                              : ''
                          }>
                          <img
                            id="mv-btnSelectOperationModeDry"
                            className="schedule__editDialog__contents__action__mode__toggleButton__image"
                            src={
                              operationMode === 'dehumidify'
                                ? iconWhite.DRYING.src
                                : iconBlack.DRYING.src
                            }
                            alt={
                              operationMode === 'dehumidify'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.DRYING.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.DRYING.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                    {/** 設定温度 */}
                    <Stack spacing={1}>
                      <Box>
                        {trans('ScheduleSettingDialog.action.setTemp')} :
                      </Box>
                      {/** 設定温度有無切替スイッチ */}
                      <Switch
                        checked={enabledTempSlider}
                        onChange={handleNoSetTemperature}
                      />
                      {/* 暖房時  ：16°C～30°C（0.5°C刻み） */}
                      {/* 暖房以外：18°C～30°C（0.5°C刻み） */}
                      <Box className="schedule__editDialog__contents__action__setTemp">
                        <Fade in={enabledTempSlider} appear={false}>
                          <Slider
                            aria-label="Set temperature"
                            value={Number(presetTemp)}
                            valueLabelDisplay="on"
                            valueLabelFormat={Number(presetTemp).toFixed(1)}
                            marks={
                              operationMode === 'heating'
                                ? sliderMarks.SCHEDULE_HEATING_SET_TEMP
                                : sliderMarks.SCHEDULE_SET_TEMP
                            }
                            min={
                              operationMode === 'heating'
                                ? scheduleTemp.SET_TEMP_HEATING_MIN
                                : scheduleTemp.SET_TEMP_MIN
                            }
                            max={scheduleTemp.SET_TEMP_MAX}
                            step={0.5}
                            onChange={handlePresetTemperatureChange}
                            onChangeCommitted={
                              handlePresetTemperatureChangeCommitted
                            }
                            disableSwap
                          />
                        </Fade>
                      </Box>
                    </Stack>
                    {/** 風量 */}
                    <Stack spacing={1}>
                      <Box>{trans('ScheduleSettingDialog.action.fan')} :</Box>
                      <ToggleButtonGroup
                        value={airFlow}
                        exclusive
                        onChange={handleAirFlowChange}
                        aria-label="Fan speed change"
                        id="mv-btnSelectOperationAirFlow"
                        className="schedule__editDialog__contents__action__fan__toggleButton">
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.fan.auto'
                          )}`}
                          value="auto"
                          aria-label="auto"
                          id="mv-btnSelectOperationAirFlowAuto"
                          className={
                            airFlow === 'auto'
                              ? 'schedule__editDialog__contents__action__fan__toggleButton--selected'
                              : ''
                          }>
                          <img
                            className="schedule__editDialog__contents__action__fan__toggleButton__image"
                            src={
                              airFlow === 'auto'
                                ? iconWhite.FAN_SPEED_AUTO.src
                                : iconBlack.FAN_SPEED_AUTO.src
                            }
                            alt={
                              airFlow === 'auto'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_AUTO.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.fan.low'
                          )}`}
                          value="low"
                          aria-label="low"
                          id="mv-btnSelectOperationAirFlowLow"
                          className={
                            airFlow === 'low'
                              ? 'schedule__editDialog__contents__action__fan__toggleButton--selected'
                              : ''
                          }>
                          <img
                            className="schedule__editDialog__contents__action__fan__toggleButton__image"
                            src={
                              airFlow === 'low'
                                ? iconWhite.FAN_SPEED_1.src
                                : iconBlack.FAN_SPEED_1.src
                            }
                            alt={
                              airFlow === 'low'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_1.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.fan.medium'
                          )}`}
                          value="middle"
                          aria-label="Medium"
                          id="mv-btnSelectOperationAirFlowMiddle"
                          className={
                            airFlow === 'middle'
                              ? 'schedule__editDialog__contents__action__fan__toggleButton--selected'
                              : ''
                          }>
                          <img
                            className="schedule__editDialog__contents__action__fan__toggleButton__image"
                            src={
                              airFlow === 'middle'
                                ? iconWhite.FAN_SPEED_2.src
                                : iconBlack.FAN_SPEED_2.src
                            }
                            alt={
                              airFlow === 'middle'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_2.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.fan.high'
                          )}`}
                          value="high"
                          aria-label="high"
                          id="mv-btnSelectOperationAirFlowHigh"
                          className={
                            airFlow === 'high'
                              ? 'schedule__editDialog__contents__action__fan__toggleButton--selected'
                              : ''
                          }>
                          <img
                            className="schedule__editDialog__contents__action__fan__toggleButton__image"
                            src={
                              airFlow === 'high'
                                ? iconWhite.FAN_SPEED_3.src
                                : iconBlack.FAN_SPEED_3.src
                            }
                            alt={
                              airFlow === 'high'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_3.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                        <ToggleButton
                          title={`${trans(
                            'ScheduleSettingDialog.toggleButton.fan.powerful'
                          )}`}
                          value="powerful"
                          aria-label="powerful"
                          id="mv-btnSelectOperationAirFlowPowerful"
                          className={
                            airFlow === 'powerful'
                              ? 'schedule__editDialog__contents__action__fan__toggleButton--selected'
                              : ''
                          }>
                          <img
                            className="schedule__editDialog__contents__action__fan__toggleButton__image"
                            src={
                              airFlow === 'powerful'
                                ? iconWhite.FAN_SPEED_4.src
                                : iconBlack.FAN_SPEED_4.src
                            }
                            alt={
                              airFlow === 'powerful'
                                ? `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
                                  )}`
                                : `${trans(
                                    `ScheduleSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_4.alt}`
                                  )}`
                            }
                          />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            id={'mv-btnScheduleSettingCancel'}
            onClick={handleClose}
            disabled={editAPIWait}
            buttonText={trans('ScheduleSettingDialog.cancel')}
          />
          <PrimaryButton
            id={'mv-btnScheduleSettingApply'}
            onClick={handleOK}
            disabled={editAPIWait || isInvalidInput()}
            apiWait={editAPIWait}
            buttonText={trans('ScheduleSettingDialog.ok')}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
      />
      {/* 時刻重複エラーダイアログ */}
      <Dialog
        open={isOpenDuplicateErrorDialog}
        onClose={handleDuplicateErrorClose}>
        <DialogTitle
          id="form-dialog-title"
          className="schedule__errorDialog__title">
          {trans('ScheduleSettingDialog.errorDlg.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            <p className="schedule__errorDialog__contents">
              {trans('ScheduleSettingDialog.errorDlg.msg')}
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            onClick={handleDuplicateErrorClose}
            buttonText={trans('ScheduleSettingDialog.errorDlg.buttonTxt.ok')}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ScheduleSettingDialog;
